import React, {useEffect, useRef, useState, useMemo} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {FmlxIcon, FmlxTooltip} from 'fmlx-common-ui';
import {useSelector} from 'react-redux';
import {find} from '../../../lib/objectHelper';
import FmlxNumericInputWithButton from '../../../components/FmlxNumericInputWithButton';

import LabwarePreview from './LabwarePreview';

import {NormalizationPreviewWellStatus, NormalizationType, TrayType} from '../../../constants/dataEnum';
import {isColorLight} from '../../../lib/colorHelper';

import './InputTargetConcentration.scss';
import withIntl from '../../../lib/withIntl';
import {convertRowToAlphabet} from '../../../stores/business/commonTaskListBusiness';

const getTextColor = (colorHex) => {
  try {
    return isColorLight(colorHex) ? '#000000' : '#ffffff';
  } catch (err) {
    return '#000000';
  }
};

const getBackgroundColor = (status, opacity = 1) => {
  const opacityHex = Math.ceil(opacity * 255).toString(16);
  let color;
  switch (status) {
    case NormalizationPreviewWellStatus.INVALID:
      color = '#ff5252';
      break;
    case NormalizationPreviewWellStatus.SKIPPED:
      color = '#FFC200';
      break;
    case NormalizationPreviewWellStatus.NORMAL:
      color = `#006B2E${opacityHex}`;
      break;
    default:
      color = '#fff';
      break;
  }
  return color;
};

const TableCell = styled.td`
  min-width: ${(prop) => `${(prop.width - 20) / 4}px`};
  /* min-width: 170px; */
  font-weight: ${(prop) => (prop.highlightData ? 'bold' : 'unset')};
  font-size: ${(prop) => (prop.highlightData ? '18px' : 'unset')};
  background: ${(prop) => {
    const opacity = prop.level ? prop.level : 1;
    const status = prop.isInvalid ? NormalizationPreviewWellStatus.SKIPPED : prop.status;

    return getBackgroundColor(status, opacity);
  }};
  color: ${(prop) => {
    let color = '#000000';

    if (prop.status) {
      const opacity = prop.level ? prop.level : 1;
      color = getTextColor(getBackgroundColor(prop.status, opacity));
    }
    return color;
  }};
`;

function InputTargetConcentration({intl, handleChangeTargetConcentration, handleChangeCurrentVolume}) {
  const {labwareState, normalizationState, protocolState, configState} = useSelector((state) => state);
  const {sampleLabwareID, targetConcentration, previewData, currentVolume, type} = normalizationState;
  const {lastOpenedProtocol} = protocolState;
  const {recommendedAspirateHighVolume} = configState;
  const isHighVolumeProtocol = lastOpenedProtocol?.trayType === TrayType.HIGH_VOLUME_CADDY;
  const selectedLabware = useMemo(
    () => find(labwareState, {LabwareID: sampleLabwareID}) ?? null,
    [labwareState, sampleLabwareID],
  );
  const [skippedCount, setSkippedCount] = useState(0);
  const [invalidCount, setInvalidCount] = useState(0);
  const [tableWrapperHeight, setTableWrapperHeight] = useState(0);
  const [tableCellHeight, setTableCellHeight] = useState(0);
  const [selectedLabwareWell, setSelectedLabwareWell] = useState();

  const tableWrapperRef = useRef(null);
  const tableCellRef = useRef(null);

  useEffect(() => {
    if (tableWrapperRef.current && tableCellRef.current) {
      const {height: cellHeight} = tableCellRef.current.getBoundingClientRect();
      const {width: tableWidth} = tableWrapperRef.current.getBoundingClientRect();

      setTableCellHeight(cellHeight);
      setTableWrapperHeight(tableWidth);
    }
  }, []);

  const handleClickLabwareWell = (index) => {
    tableWrapperRef.current.scrollTop = index * tableCellHeight;
    if (selectedLabwareWell !== index) {
      setSelectedLabwareWell(index);
    } else {
      setSelectedLabwareWell(null);
    }
  };

  const setInvalidAndSkippedCount = () => {
    let invalid = 0;
    let skipped = 0;
    previewData.forEach((data) => {
      if (data.status === NormalizationPreviewWellStatus.INVALID) {
        invalid += 1;
      }
      if (data.status === NormalizationPreviewWellStatus.SKIPPED) {
        skipped += 1;
      }
    });

    setInvalidCount(invalid);
    setSkippedCount(skipped);
  };

  useEffect(() => {
    setInvalidAndSkippedCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewData]);

  const getMaxMinData = () => {
    const data = previewData.map((row) => row.volume).filter((item) => typeof item === 'number' && item !== 0);

    let maxData = 0;
    let minData = 0;

    if (data.length) {
      maxData = Math.max(...data);
      minData = Math.min(...data);
    }

    return [maxData, minData];
  };

  const generateTableData = () => {
    const elements = [];
    if (previewData.length) {
      elements.push(
        <tr key="header">
          <TableCell className="table-cell row-header" width={tableWrapperHeight ?? 0}>
            Well
          </TableCell>
          <TableCell className="table-cell row-header" width={tableWrapperHeight ?? 0}>
            Current Concentration
          </TableCell>
          <TableCell className="table-cell row-header" width={tableWrapperHeight ?? 0}>
            Target Concentration
          </TableCell>
          <TableCell className="table-cell row-header" width={tableWrapperHeight ?? 0}>
            <div className="with-tooltip">
              {type === NormalizationType.FIXED_SAMPLE_VOLUME ? 'Diluent Volume (μL)' : 'Sample Volume (μL)'}
              {isHighVolumeProtocol && (
                <FmlxTooltip
                  title={intl.formatMessage(
                    {
                      id: `protocolEditor.highCV.infoNormalization.${type === NormalizationType.FIXED_SAMPLE_VOLUME ? 'fixedSample' : 'fixedDiluent'}`,
                    },
                    {recommendedCapacity: recommendedAspirateHighVolume.value},
                  )}
                >
                  <span>
                    <FmlxIcon name="Info" variant="secondary" customColor="#006AF5" size="sm" />
                  </span>
                </FmlxTooltip>
              )}
            </div>
          </TableCell>
        </tr>,
      );
      for (let i = 0; i < previewData.length; i += 1) {
        const currentConcentration = previewData[i].reagentCurrentConcentration;
        const diluentVolume = previewData[i].volume;
        const {status} = previewData[i];
        const row = convertRowToAlphabet(previewData[i].row);
        const column = previewData[i].col;
        const [maxData, minData] = getMaxMinData();

        const level = (diluentVolume + 1 - minData) / (maxData + 1 - minData);
        const isValidCurrentConcentration =
          !Number.isNaN(Number(currentConcentration)) &&
          currentConcentration !== Infinity &&
          currentConcentration !== 'Zero';

        elements.push(
          <tr key={i} className="table-row">
            <TableCell
              className="table-cell"
              highlightData={selectedLabwareWell === i + 1}
              width={tableWrapperHeight ?? 0}
              isInvalid={!isValidCurrentConcentration}
            >
              {`${row}${column}`}
            </TableCell>
            <TableCell
              className="table-cell"
              highlightData={selectedLabwareWell === i + 1}
              width={tableWrapperHeight ?? 0}
              isInvalid={!isValidCurrentConcentration}
            >
              {currentConcentration}
            </TableCell>
            <TableCell
              className="table-cell"
              highlightData={selectedLabwareWell === i + 1}
              width={tableWrapperHeight ?? 0}
              isInvalid={!isValidCurrentConcentration}
            >
              {targetConcentration === 0 ? '-' : targetConcentration}
            </TableCell>
            <TableCell
              ref={tableCellRef}
              className="table-cell"
              level={level}
              status={targetConcentration === 0 ? '' : status}
              highlightData={selectedLabwareWell === i + 1}
              width={tableWrapperHeight ?? 0}
              isInvalid={!isValidCurrentConcentration}
            >
              {targetConcentration === 0 ? '-' : diluentVolume}
            </TableCell>
          </tr>,
        );
      }
    }

    return elements;
  };

  return (
    <div className="input-target-concentration">
      <div className="input-target-concentration__panel">
        <div className="input-target-concentration__panel__top-section">
          <div className="description">
            <div className="description__label">
              <div className="description__label--invalid" />
              <div>
                <span className="description__label--text">Invalid</span>
                &nbsp;
                <FmlxTooltip
                  title={intl.formatMessage({id: `protocolEditor.normalization.inputTargetConcentration.invalid`})}
                >
                  <span>
                    <FmlxIcon name="InfoOutline" size="xs" />
                  </span>
                </FmlxTooltip>
              </div>
              <span className="description__label--text">{invalidCount}</span>
            </div>
          </div>
          <div className="description">
            <div className="description__label">
              <div className="description__label--skipped" />
              <div>
                <span className="description__label--text">Skipped</span>
                &nbsp;
                <FmlxTooltip
                  title={intl.formatMessage({id: 'protocolEditor.normalization.inputTargetConcentration.skipped'})}
                >
                  <span>
                    <FmlxIcon name="InfoOutline" size="xs" />
                  </span>
                </FmlxTooltip>
              </div>
              <span className="description__label--text">{skippedCount}</span>
            </div>
          </div>
        </div>
        <div className="input-target-concentration__panel__bottom-section">
          <LabwarePreview
            handleClick={handleClickLabwareWell}
            normalizationState={normalizationState}
            showDiluent
            selectedLabwareWell={selectedLabwareWell}
            labwareState={labwareState}
          />
        </div>
      </div>
      <div className="input-target-concentration__panel summary">
        <div className="summary__top-section">
          <div className="summary__bottom-section__item target-concentration">
            <span className="target-concentration__label">
              {type === NormalizationType.FIXED_SAMPLE_VOLUME
                ? 'Fixed Sample Volume per well (μL)'
                : 'Fixed Diluent Volume per well (μL)'}
            </span>
            <FmlxNumericInputWithButton
              inputClassName="target-concentration__input"
              placeholder="0"
              defaultValue={currentVolume}
              value={currentVolume}
              stepValue={0.1}
              decimalPrecision={1}
              max={selectedLabware.workingCapacity}
              onChange={(value) => {
                handleChangeCurrentVolume(value);
              }}
              allowDecimal
            />
          </div>

          <div className="summary__bottom-section__item target-concentration">
            <span className="target-concentration__label">Target Concentration</span>
            <FmlxNumericInputWithButton
              inputClassName="target-concentration__input"
              plusButtonClassName="target-concentration__plus-minus-button"
              minusButtonClassName="target-concentration__plus-minus-button"
              placeholder="0"
              defaultValue={targetConcentration}
              value={targetConcentration}
              stepValue={0.1}
              decimalPrecision={1}
              onChange={(value) => {
                handleChangeTargetConcentration(value);
              }}
              allowDecimal
            />
          </div>
        </div>

        <div ref={tableWrapperRef} className="summary__bottom-section">
          <table>
            <tbody>{generateTableData()}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

InputTargetConcentration.propTypes = {
  handleChangeTargetConcentration: PropTypes.func,
  handleChangeCurrentVolume: PropTypes.func,
  intl: PropTypes.any.isRequired,
};

InputTargetConcentration.defaultProps = {
  handleChangeTargetConcentration: () => {},
  handleChangeCurrentVolume: () => {},
};

export default withIntl(InputTargetConcentration);
